import { Box, HStack, Text, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { EmailLogFragment } from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { dateTime } from "@src/utils/formatters";
import { isAfter, isBefore } from "date-fns";
import { observer } from "mobx-react-lite";
import { Fragment, type FunctionComponent, useMemo } from "react";

type Props = {
  logs: EmailLogFragment[];
  type: "outgoingProformaInvoice" | "outgoingInvoice";
};

export const EmailLogs: FunctionComponent<Props> = observer(function EmailLogs({
  logs,
  type,
}) {
  const email = useMemo(() => {
    return logs
      .toSorted((a, b) =>
        // Newest first
        isBefore(new Date(a.sent_at), new Date(b.sent_at)) ? 1 : -1,
      )
      .find((log) => log.type === type);
  }, [logs, type]);

  const reminders = logs
    .filter((l) => l.type === `${type}PaymentReminder`)
    .toSorted((a, b) =>
      // Oldest first
      isAfter(new Date(a.sent_at), new Date(b.sent_at)) ? 1 : -1,
    );

  const thankYouMail = logs.find((log) => log.type === "invoiceSendThankYou");

  return (
    <Fragment>
      <HStack pos="relative" align="center" ml="2" spacing="0">
        {email && (
          <Tooltip
            label={
              <EmailTooltip
                title={t`Invoice sent`}
                email={email}
                reminders={reminders}
              />
            }
            placement="top"
          >
            <Box zIndex="1">
              <Icon
                name="mail-02"
                color="green.500"
                bg="white"
                _groupHover={{ bg: "gray.50" }}
              />
            </Box>
          </Tooltip>
        )}

        {thankYouMail && (
          <Tooltip
            label={
              <EmailTooltip
                title={t`Payment confirmation sent`}
                email={thankYouMail}
              />
            }
            placement="top"
          >
            <Box
              zIndex="0"
              _hover={{
                zIndex: "2",
              }}
            >
              <Icon
                name="mail-02"
                color="gray.500"
                ml={email ? "-1.5" : undefined}
                bg="white"
                _hover={{
                  bg: "gray.50",
                }}
              />
            </Box>
          </Tooltip>
        )}
      </HStack>

      {logs.length
        ? !email?.accepted_delivery && (
            <Tooltip
              label={t`Invoice delivery hasn’t been accepted yet.`}
              placement="top"
              shouldWrapChildren
            >
              <Icon name="info-circle" color="red.500" ml="1" />
            </Tooltip>
          )
        : null}
    </Fragment>
  );
});

type EmailTooltipProps = {
  title: string;
  email: EmailLogFragment;
  reminders?: EmailLogFragment[];
};
const EmailTooltip = observer(function EmailTooltip({
  title,
  email,
  reminders,
}: EmailTooltipProps) {
  const sentAt = dateTime.format(new Date(email?.sent_at ?? ""));
  const recipientLogs = email?.recipientLog ?? [];
  const emails = recipientLogs.length > 0 ? recipientLogs : email?.recipients;
  const recipients = emails
    ?.map((email) =>
      "recipient" in email
        ? `${email.recipient} (${email.status})`
        : email.email,
    )
    .join(", ");

  return (
    <Box fontSize="xs">
      <Text mb="1" fontSize="sm">
        <strong>{title}</strong>
      </Text>

      <Text>
        <strong>
          <Trans>Date:</Trans>
        </strong>
        &nbsp;
        {sentAt}
      </Text>

      <Text mb={reminders?.length ? "1" : undefined}>
        <strong>
          <Trans>To:</Trans>
        </strong>
        &nbsp;
        {recipients}
      </Text>

      {reminders?.map((reminder, index) => (
        <Text key={reminder.id}>
          <strong>
            {index + 1}. <Trans>reminder:</Trans>
          </strong>
          &nbsp;
          {reminder.sent_at ? dateTime.format(reminder.sent_at) : ""}
        </Text>
      ))}
    </Box>
  );
});
